import React, {useState} from 'react';
import './NewCollection.css';
import {useTranslation} from 'react-i18next';
import {Col, Container, Form, Modal, Row} from 'react-bootstrap';
import useCollectionModification from '../../hooks/useCollectionModification';
import FormDialog from '../dialog/FormDialog';
import CollectionName from './name/CollectionName';
import CollectionDescription from './description/CollectionDescription';
import CollectionPublicity from './publicity/CollectionPublicity';
import CollectionManagementRights from './management-rights/CollectionManagementRights';
import useUser from '../../hooks/useUser';
import CollectionMoodleCourses from './moodle-courses/CollectionMoodleCourses';
import NewCollectionFooter from './NewCollectionFooter';
import useCollectionSave from '../../hooks/collection/useCollectionSave';
import {ProgressStatus} from '../../Constants';
import useCollectionValidation from '../../hooks/validation/collection/useCollectionValidation';
import HyButton from '../utilities/HyButton';
import useCollectionDropdown from '../../hooks/collection/useCollectionDropdown';
import CollectionCreators from './creator/CollectionCreators';
import CollectionKeywords from './keyword/CollectionKeywords';
import ContentTypes from '../form/ContentTypes';
import ScienceDropdown from '../form/ScienceDropdown';
import FacultiesDepartmentsDropdown from '../form/FacultiesDepartmentsDropdown';
import HelpDialog from "../dialog/HelpDialog";

const NewCollection = () => {
    const {t} = useTranslation();
    const [user] = useUser();
    const [showForm, setShowForm] = useState(false);
    const [progress, save, resetProgress] = useCollectionSave();
    const [isValid, messages, validate] = useCollectionValidation([
        'title', 'description', 'published', 'creators', 'persons', 'contentType', 'science', 'facultyDepartment'
    ]);
    const [_collectionDropDown, _loading, reloadCollectionDropdown] = useCollectionDropdown(true);

    const defaultPersons = [user.eppn];
    const defaultCreators = [{...user, userName: user.eppn}];
    const emptyCollection = {
        identifier: '',
        title: '',
        description: '',
        iamgroups: [],
        persons: defaultPersons,
        published: '',
        moodleNumbers: [],
        creators: defaultCreators,
        keywords: [],
        contentType: '',
        science: '',
        facultyDepartment: '',
        isCreatorHidden: '',
    };

    const [collection, onChange, modified, undo] = useCollectionModification(
        emptyCollection, validate, resetProgress
    );

    const publisherUserNames = (collection?.creators || [])
        .map(creator => typeof creator === 'string' ? creator : creator?.userName)
        .filter(Boolean);
    const administrators = [...new Set([...(collection?.persons || []), ...publisherUserNames])];

    const handleCreatorsChange = (creators) => {
        onChange({
            creators,
            persons: [...new Set([...(collection?.persons || []), ...creators
                .map(creator => typeof creator === 'string' ? creator : creator?.userName)
                .filter(Boolean)])]
        });
    };

    const theButton = (
        <HyButton
            variant="primary"
            className="new-collection-button"
            onClick={() => setShowForm(true)}
        >
            {t('new_collection_button')}
        </HyButton>
    );

    const saveInProgress = progress.status === ProgressStatus.NEW_COLLECTION.SENDING;
    const errorOccurred = progress.status === ProgressStatus.NEW_COLLECTION.ERROR;
    const savingDone = progress.status === ProgressStatus.NEW_COLLECTION.DONE;
    const fieldsDisabled = saveInProgress || errorOccurred || savingDone;

    const closeButton = {closeButton: true};

    const hide = () => {
        undo();
        resetProgress();
        setShowForm(false);
        reloadCollectionDropdown();
    };

    const tryAgain = save;

    const clearForm = async () => {
        await undo();
        resetProgress();
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (progress.status === ProgressStatus.NEW_COLLECTION.NOT_STARTED) {
            await save({
                ...collection,
                persons: [...new Set([...(collection?.persons || []), ...publisherUserNames])]
            });
        } else if (progress.status === ProgressStatus.NEW_COLLECTION.DONE) {
            await clearForm();
        } else if (progress.status === ProgressStatus.NEW_COLLECTION.ERROR) {
            resetProgress();
            await tryAgain({
                ...collection,
                persons: [...new Set([...(collection?.persons || []), ...publisherUserNames])]
            });
        }
    };

    return (
        <FormDialog
            hide={hide}
            showComponent={theButton}
            show={showForm}
            size="xl"
            touched={modified && progress.status !== ProgressStatus.NEW_COLLECTION.DONE}
        >
            <Modal.Header {...closeButton}>{t('new_collection_form_header')}</Modal.Header>
            <Form className="new-collection-form ms-3 me-3" onSubmit={onSubmit}>
                <Modal.Body>
                    <Row className="mb-2">
                        <Col className="ps-1">
                            <CollectionName name={collection?.title} onChange={(title) => onChange('title', title)}
                                            disabled={fieldsDisabled} message={messages?.title}/>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col className="ps-1">
                            <CollectionDescription description={collection?.description}
                                                   onChange={(description) => onChange('description', description)}
                                                   disabled={fieldsDisabled} message={messages?.description}/>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col className="ps-1">
                            <CollectionPublicity publicity={collection?.published}
                                                 onChange={(publicity) => onChange('published', publicity)}
                                                 disabled={fieldsDisabled} messages={messages?.published}/>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Row>
                            <Col className="ps-2 m-0 collection-form-section-publisher-heading">
                                <h3>
                                    {t('collection_form_section_publisher')}
                                </h3>
                                <HelpDialog label={t('collection_management_creator_help_label')} className="collection-management-creator-help-label">
                                    {t('collection_management_creator_help_content')}
                                </HelpDialog>
                            </Col>
                            <Row>
                                <Col className="collection-management-creator-subheading">
                                    <div>
                                        {t('collection_management_creator_subheading')}
                                    </div>
                                </Col>
                            </Row>
                        </Row>
                        <Col className="ps-1 mb-2 mt-1" xs={12} md={6}>
                            <CollectionCreators
                                creators={collection?.creators}
                                onChange={handleCreatorsChange}
                                disabled={fieldsDisabled} messages={messages?.creators}
                                isCreatorHidden={Boolean(collection?.isCreatorHidden)}
                                onVisibilityChange={(isCreatorHidden) => onChange('isCreatorHidden', isCreatorHidden)}
                            />
                        </Col>
                        <Col className="ps-1 mt-2" xs={12} md={6}>
                            <FacultiesDepartmentsDropdown
                                selected={collection.facultyDepartment}
                                onChange={(facultyDepartment) => onChange('facultyDepartment', facultyDepartment)}
                                disabled={fieldsDisabled}
                                message={messages?.facultyDepartment}
                                required
                            />
                        </Col>
                    </Row>
                    <Container className="ps-0">
                        <Row>
                            <Col className="ps-0">
                                <CollectionMoodleCourses
                                    moodleNumbers={collection?.moodleNumbers}
                                    onMoodleNumberChange={(moodleNumbers) => onChange('moodleNumbers', moodleNumbers)}
                                    disabled={fieldsDisabled}/>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="ps-1">
                                <CollectionManagementRights
                                    users={administrators}
                                    protectedUsers={publisherUserNames}
                                    groups={collection?.iamgroups}
                                    onUserChange={(persons) => onChange('persons', [...new Set([...persons, ...publisherUserNames])])}
                                    onGroupChange={(groups) => onChange({
                                        iamgroups: groups,
                                        persons: [...new Set([...(collection?.persons || []), ...publisherUserNames])]
                                    })}
                                    message={messages.persons}
                                    disabled={fieldsDisabled}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="ps-0">
                                <CollectionKeywords keywords={collection?.keywords}
                                                    onKeywordChange={(keywords) => onChange('keywords', keywords)}
                                                    disabled={fieldsDisabled}/>
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col className="ps-1">
                                <ContentTypes
                                    selected={collection.contentType}
                                    onChange={(contentType) => onChange('contentType', contentType)}
                                    disabled={fieldsDisabled} message={messages?.contentType}
                                    required={true}
                                    defaultOptionLabelKey="collection_content_type_option_default_select"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="ps-1">
                                <ScienceDropdown className="new-collection-science-dropdown"
                                    selected={collection.science}
                                    onChange={(science) => onChange('science', science)}
                                    disabled={fieldsDisabled} message={messages?.science}
                                    required={true}
                                />
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer className="ps-0 pe-0">
                    <NewCollectionFooter
                        onCancel={hide}
                        progress={progress}
                        isValid={isValid}
                    />
                </Modal.Footer>
            </Form>
        </FormDialog>
    );
};

NewCollection.propTypes = {};

export default NewCollection;
